import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import InfiniteScroll from "src/components/infiniteScroll";
import { DealRoomContext } from "../../components/context";
import UserDetails, { PlaceholderUserDetails } from "../../components/chat/userDetails";
import ChatContainer from "../../components/chat/chatContainer";
import { fetchDealRoomEngageChatUsers } from "src/redux/actions/dealRoom";
import EmptyMessage from "src/components/emptyMessage";

import styles from './styles.module.scss';
import { DealRoomEngageChatUser } from "src/types/dealRoom";
import { useDealRoomAdoption } from "src/redux/actions/adoption/dealRoom";
import { DEAL_ROOM } from "src/constants/adoption/sourceTypes";
const NoDiscussionImage = "/images/dealRoom/no_discussion.svg";

const DealRoomEngagePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dealRoom } = useContext(DealRoomContext);
  const dealRoomId = _.get(dealRoom, 'id', '');
  const { conversationId } = useParams();

  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "";
  const { trackDealRoomEngageSectionViewed } = useDealRoomAdoption();

  const chatUsersData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom.${_.get(dealRoom, 'id', '')}.engage.chatUsers`, null);
  });

  const chatUsers = _.get(chatUsersData, 'items', []);
  const pagination = _.get(chatUsersData, 'pagination', {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 20,
    totalRecordsCount: 0,
    hasMoreItems: false
  });
  const loading = _.get(chatUsersData, 'loading', false);
  const loaded = _.get(chatUsersData, 'loaded', false);

  const { currentPage, totalPages } = pagination;

  useEffect(() => {
    const parentContainer = document.getElementById('app-body');
    if (parentContainer) {
      parentContainer.scrollTop = 220;
    }

    const validSource = source?.length > 0 ? source : DEAL_ROOM;
    trackDealRoomEngageSectionViewed({ source_cta: validSource });

    return (() => {
      if (parentContainer) {
        parentContainer.scrollTop = 0;
      }
    })

  }, []);

  useEffect(() => {
    if(dealRoomId){
      fetchChatUserList(dealRoomId, 0);
    }

  }, [dealRoomId])

  // inital request to fetch all chat user list of particular dealroom
  useEffect(() => {
    if (!loading && !loaded) {
      fetchChatUserList(dealRoomId ?? '', currentPage + 1);
    }
  }, [loading, loaded]);

  // navigate to the first chat of the dealroom if any
  useEffect(() => {
    if (!conversationId && _.size(chatUsers) > 0) {
      navigate(`/deal-room/${dealRoomId}/engage/${_.get(chatUsers[0], 'id', '')}`)
    }
  }, [chatUsers]);

  const fetchChatUserList = (dealRoomId: string, page: number) => {
    dispatch(fetchDealRoomEngageChatUsers({
      dealRoomId: dealRoomId,
      page: page,
    }));
  }

  const loadMoreChatUsers = () => {
    const nextPage = currentPage + 1;

    if (loading || nextPage > totalPages) {
      return;
    }

    fetchChatUserList(dealRoomId, nextPage);
  }

  return (
    <div className={styles.engageContainer}>
      <div className={styles.chatUserListContainer}>
        {chatUsers &&
          <InfiniteScroll
            className={styles.chatUserList}
            hasMore={_.get(pagination, 'hasMoreItems', false)}
            loadMore={loadMoreChatUsers}
            loading={loading}
            loader={() => {
              if (_.size(chatUsers) <= 0) {
                return (
                  <>
                    <PlaceholderUserDetails />
                    <PlaceholderUserDetails />
                    <PlaceholderUserDetails />
                  </>
                );
              }
            }}
          >
            {
              _.size(chatUsers) > 0 ? (
                _.map(chatUsers, (chatUser: DealRoomEngageChatUser) => {
                  return (
                    <UserDetails user={chatUser} showHighlights={true} />
                  )
                })
              ) : (<></>)
            }
          </InfiniteScroll>
        }
      </div>
      {conversationId && loaded ? (
        <ChatContainer />
      ) : (
          <div className={styles.noChatSelectedContainer}>
            {!loading && <EmptyMessage
              className={styles.noChatSelected}
              image={NoDiscussionImage}
              heading="No conversations yet. Share this deal room with your buyers to engage with them here."
              message=""
            />}
            {loading && <p>Loading...</p>}
          </div>
      )}
    </div>
  )
}


export default DealRoomEngagePage;